/**
 * Range and cursor manipulation utilities
 * 
 * This module provides utilities for:
 * - Walking through text nodes and variable spans
 * - Getting cursor position within elements
 * - Creating ranges at specific offsets
 * - Managing selection ranges
 */

import {isVariableSpan} from './elementHelpers';

function getOffsetWhenContainerIsElement(element: HTMLElement, offset: number): number {
    if(offset === 0) return 0;
    let charCount = 0;
    const limit = Math.min(offset, element.childNodes.length);
    for(let i = 0; i < limit; i++) {
        const child = element.childNodes[i];
        charCount += isVariableSpan(child) ? 1 : (child.textContent?.length || 0);
    }
    return charCount;
}

export function walkTextAndVariableSpans(
    root: Node,
    cb: (node: Node, isVariableSpan: boolean, textLength: number) => boolean
): boolean {
    if(root.nodeType === Node.TEXT_NODE) {
        const len = root.textContent?.length ?? 0;
        return cb(root, false, len);
    }
    
    if(isVariableSpan(root)) {
        const len = root.textContent?.length ?? 1;
        return cb(root, true, len);
    }
    
    for(let i = 0; i < root.childNodes.length; i++) {
        const interrupted = walkTextAndVariableSpans(root.childNodes[i], cb);
        if(interrupted) return true;
    }
    
    return false;
}

export function getCursorOffsetInElement(element: HTMLElement): number {
    if(!element) return 0;
    
    const selection = window.getSelection();
    if(!selection?.rangeCount) return 0;
    
    const range = selection.getRangeAt(0);
    if(!range?.startContainer || !element.contains(range.startContainer as Node)) {
        return 0;
    }

    // Browser (e.g. Chrome Ctrl+A) may set startContainer to the element itself
    // rather than a child text node. Handle by counting child character lengths.
    if(range.startContainer === element) {
        return getOffsetWhenContainerIsElement(element, range.startOffset);
    }
    
    let offset = 0;
    walkTextAndVariableSpans(element, (node, isVar, len) => {
        if(isVar) {
            if(node === range.startContainer || (node as Node).contains(range.startContainer)) {
                return true;
            }
            offset += 1;
        } else {
            if(node === range.startContainer) {
                offset += Math.min(range.startOffset, len);
                return true;
            }
            offset += len;
        }
        return false;
    });
    
    return offset;
}

export function getSelectionEndOffsetInElement(element: HTMLElement): number {
    if(!element) return 0;
    
    const selection = window.getSelection();
    if(!selection?.rangeCount) return 0;
    
    const range = selection.getRangeAt(0);
    if(!range?.endContainer || !element.contains(range.endContainer as Node)) {
        return 0;
    }
    
    // Browser (e.g. Chrome Ctrl+A) may set endContainer to the element itself
    // rather than a child text node. Handle by counting child character lengths.
    if(range.endContainer === element) {
        return getOffsetWhenContainerIsElement(element, range.endOffset);
    }
    
    let offset = 0;
    walkTextAndVariableSpans(element, (node, isVar, len) => {
        if(isVar) {
            if(node === range.endContainer || (node as Node).contains(range.endContainer)) {
                return true;
            }
            offset += 1;
        } else {
            if(node === range.endContainer) {
                offset += Math.min(range.endOffset, len);
                return true;
            }
            offset += len;
        }
        return false;
    });
    
    return offset;
}

function createRangeAtEnd(element: HTMLElement): Range {
    const range = document.createRange();
    const lastChild = element.lastChild;
    
    if(lastChild?.nodeType === Node.TEXT_NODE) {
        range.setStart(lastChild, lastChild.textContent?.length || 0);
    } else if(lastChild) {
        range.setStartAfter(lastChild);
    } else {
        range.selectNodeContents(element);
    }
    
    range.collapse(true);
    return range;
}

export function createRangeAtOffset(element: HTMLElement, characterOffset: number): Range {
    if(!element) return document.createRange();
    
    if(characterOffset < 0) {
        characterOffset = 0;
    }
    
    const range = document.createRange();
    let passed = 0;
    
    const found = walkTextAndVariableSpans(element, (node, isVar, len) => {
        if(isVar) {
            if(passed === characterOffset) {
                range.setStartBefore(node);
                range.collapse(true);
                return true;
            }
            passed += 1;
        } else {
            if(passed + len >= characterOffset) {
                range.setStart(node, Math.min(characterOffset - passed, len));
                range.collapse(true);
                return true;
            }
            passed += len;
        }
        return false;
    });
    
    if(found) return range;
    
    return createRangeAtEnd(element);
}

export function getRangeInElement(
    element: HTMLElement, 
    savedCursorOffset?: number | null,
    shouldFocus: boolean = false
): Range {
    if(!element) return document.createRange();
    
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

    // Use existing selection if it's within the element
    if(range?.startContainer && element.contains(range.startContainer as Node)) {
        return range;
    }

    // Use saved cursor offset if provided
    if(typeof savedCursorOffset === 'number' && savedCursorOffset >= 0) {
        return createRangeAtOffset(element, savedCursorOffset);
    }

    // Default: place cursor at end
    if(shouldFocus) {
        element.focus();
    }
    return createRangeAtEnd(element);
}

export function applyRangeToSelection(range: Range): void {
    const selection = window.getSelection();
    if(selection) {
        selection.removeAllRanges();
        selection.addRange(range);
    }
}
